import { MetadataTable } from "@components/backoffice/MetadataTable";
import isEmpty from "lodash/isEmpty";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { Divider, List, Table } from "semantic-ui-react";

export class SpecialCollectionInfo extends Component {
  renderSimpleAuthors = () => {
    const { metadata } = this.props;
    // Access the nested metadata object
    const metadataContent = metadata.metadata || metadata;

    if (metadataContent.authors && metadataContent.authors.length > 0) {
      return metadataContent.authors
        .map((author) => {
          if (typeof author === "string") {
            return author;
          }
          return author.full_name || author.name || JSON.stringify(author);
        })
        .join(", ");
    }
    return "No authors";
  };

  renderAdvisers = () => {
    const { metadata } = this.props;
    const metadataContent = metadata.metadata || metadata;

    // Check if advisers is in the root metadata
    if (metadataContent.advisers) {
      if (Array.isArray(metadataContent.advisers)) {
        return metadataContent.advisers
          .map((adviser) => {
            if (typeof adviser === "string") {
              return adviser;
            }
            return adviser.full_name || adviser.name || JSON.stringify(adviser);
          })
          .join(", ");
      }
      return String(metadataContent.advisers);
    }

    // Check if advisor is in academic_info (singular)
    if (metadataContent.academic_info?.advisor) {
      return metadataContent.academic_info.advisor;
    }

    return "Not specified";
  };

  renderLanguages = () => {
    const { metadata } = this.props;
    const metadataContent = metadata.metadata || metadata;

    if (metadataContent.languages) {
      return (
        <Table.Row>
          <Table.Cell>Languages</Table.Cell>
          <Table.Cell>{metadataContent.languages.join(", ")}</Table.Cell>
        </Table.Row>
      );
    }
    return null;
  };

  renderKeywords() {
    const { metadata } = this.props;
    const metadataContent = metadata.metadata || metadata;

    if (metadataContent.keywords && metadataContent.keywords.length > 0) {
      return (
        <Table.Row>
          <Table.Cell>Keywords</Table.Cell>
          <Table.Cell>
            {metadataContent.keywords
              .map((keyword) => {
                if (typeof keyword === "string") return keyword;
                return (
                  keyword.value || keyword.keyword || JSON.stringify(keyword)
                );
              })
              .join(", ")}
          </Table.Cell>
        </Table.Row>
      );
    }
    return null;
  }

  renderCollectionType = () => {
    const { metadata } = this.props;
    const metadataContent = metadata.metadata || metadata;

    const collectionTypeMap = {
      GRADUATE_THESIS: "Graduate Thesis",
      UNDERGRADUATE_THESIS: "Undergraduate Thesis",
      RESEARCH_PAPER: "Research Paper",
      CAPSTONE_PROJECT: "Capstone Project",
      DISSERTATION: "Dissertation",
    };

    if (metadataContent.collection_type) {
      return (
        <Table.Row>
          <Table.Cell>Collection Type</Table.Cell>
          <Table.Cell>
            {collectionTypeMap[metadataContent.collection_type] ||
              metadataContent.collection_type}
          </Table.Cell>
        </Table.Row>
      );
    }
    return null;
  };

  prepareAcademicInfo = () => {
    const { metadata } = this.props;
    const metadataContent = metadata.metadata || metadata;
    const academicInfo = metadataContent.academic_info || {};

    return [
      { name: "Department", value: academicInfo.department },
      { name: "Program", value: academicInfo.program },
      { name: "Degree", value: academicInfo.degree },
      { name: "Advisor", value: academicInfo.advisor },
      {
        name: "Committee Members",
        value: academicInfo.committee_members?.join(", "),
      },
    ].filter((item) => item.value); // Only show rows with values
  };

  render() {
    const { metadata } = this.props;
    const metadataContent = metadata.metadata || metadata;

    return (
      <>
        <Divider horizontal>Details</Divider>
        <Table definition>
          <Table.Body>
            <Table.Row>
              <Table.Cell width={4}>Title</Table.Cell>
              <Table.Cell>{metadataContent.title || "No title"}</Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell width={4}>Alternative titles</Table.Cell>
              <Table.Cell>
                {metadataContent.alternative_titles &&
                metadataContent.alternative_titles.length > 0 ? (
                  <List bulleted>
                    {metadataContent.alternative_titles.map((entry, index) => (
                      <List.Item key={entry.value || index}>
                        <List.Content className="alternative-title">
                          {typeof entry === "string" ? entry : entry.value}
                        </List.Content>
                      </List.Item>
                    ))}
                  </List>
                ) : (
                  "No alternative titles"
                )}
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell width={4}>Authors</Table.Cell>
              <Table.Cell>{this.renderSimpleAuthors()}</Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell width={4}>Advisers</Table.Cell>
              <Table.Cell>{this.renderAdvisers()}</Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell width={4}>Publication year</Table.Cell>
              <Table.Cell>
                {metadataContent.publication_year || "Not specified"}
              </Table.Cell>
            </Table.Row>
            {this.renderCollectionType()}
            {metadataContent.abstract && (
              <Table.Row>
                <Table.Cell width={4}>Abstract</Table.Cell>
                <Table.Cell
                  style={{ maxWidth: "500px", wordWrap: "break-word" }}
                >
                  {metadataContent.abstract}
                </Table.Cell>
              </Table.Row>
            )}
            {this.renderLanguages()}
            {this.renderKeywords()}
          </Table.Body>
        </Table>

        {!isEmpty(metadataContent.academic_info) ? (
          <>
            <Divider horizontal>Academic Information</Divider>
            <MetadataTable rows={this.prepareAcademicInfo()} />
          </>
        ) : null}

        {metadataContent.urls && metadataContent.urls.length > 0 && (
          <>
            <Divider horizontal>More information</Divider>
            <Table definition>
              <Table.Body>
                {metadataContent.urls.map((url, index) => (
                  <Table.Row key={index}>
                    <Table.Cell width={4}>URL {index + 1}</Table.Cell>
                    <Table.Cell>
                      <a
                        href={url.value || url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {url.description || url.value || url}
                      </a>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </>
        )}
      </>
    );
  }
}

SpecialCollectionInfo.propTypes = {
  metadata: PropTypes.object.isRequired,
};
